import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  styled,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { ThemeSettings } from "@kahunmedical/common-configuration";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import { getV2ClinicSettings } from "fe-web-components/settings/common/getV2ClinicSettings";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import {
  DEFAULT_EMAIL_INLINE_TEMPLATE,
  DEFAULT_SMS_INLINE_TEMPLATE,
} from "fe-common/communicationDefaults";
import { useClinicState } from "../../../clinic/clinicStateContext";
import { ClinicColors } from "fe-web-components/styles/clinicTheme";
import { ClinicActionButton } from "fe-web-components/clinic/clinicActionButton";
import { useUpdateClinicConfig } from "../hooks/useUpdateClinicConfig";
import { ClinicCustomizationGeneral } from "./clinicCustomizationGeneral";
import { ClinicCustomizationTexts } from "./clinicCustomizationTexts";
import { ClinicUsers } from "../clinicUsers/clinicUsers";
import { LoadingProgress } from "fe-web-components/loader/loadingProgress";
import { useHistory, useParams, useRouteMatch } from "react-router";
import { removeTrailingSlash } from "fe-common/routes/removeTrailingSlash";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { ClinicDialog } from "../elements/clinicDialog";
import { ClinicPageWrapper } from "../elements/clinicPageWrapper";
import { MasterClinicWarning } from "fe-web-components/settings/common/MasterClinicWarning";
import { useClinicUserContext } from "../clinicUserContext";
import { useClinicMessagesContext } from "../clinicMessagesContext";

//region [[ Styles ]]

export const SectionTitle = styled("h4")({
  fontSize: 14,
  fontWeight: 700,
  margin: "0 0 8px 0",
});
export const SectionBox = styled("div")({
  backgroundColor: "#FFFFFF",
  border: `1px solid ${ClinicColors.borders}`,
  padding: 20,
  marginBottom: 32,
  flex: "1",
});
export const MessageTextArea = styled(TextField)({
  marginTop: 8,
  "& textarea": { fontSize: 13, borderColor: ClinicColors.borders },
});
export const MessageTextButtons = styled("div")({
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: 4,
  height: 22,
});
export const DefaultTemplateButton = styled(Button)({
  textTransform: "none",
  fontWeight: 400,
});
const CustomizationTabsSelect = styled(Tabs)({
  "& .MuiTabs-flexContainer": {
    gap: 50,
  },
  "& .MuiTabs-indicator": {
    height: 4,
    backgroundColor: ClinicColors.primary,
  },
});
const CustomizationTab = styled(Tab)({
  textTransform: "none",
  fontSize: 13,
  fontWeight: 700,
  padding: "20px 0",
  minWidth: 0,
  color: ClinicColors.secondary,
  opacity: 1,
  "&:hover": {
    opacity: 0.7,
  },
  "&.Mui-selected": {
    color: ClinicColors.textMain,
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export enum CustomizationTabs {
  "General" = "general",
  "TextCustomization" = "texts",
  "UserManagement" = "users",
}

export enum TemplateName {
  "SMSTemplate" = "smsTemplate",
  "EmailTemplate" = "emailTemplate",
  "CustomWelcome" = "customWelcome",
}

export interface TemplateDialogProps {
  methodName: string;
  templateName: TemplateName;
  templateDefault: string;
}

export interface ClinicCustomizationProps {}

export interface ClinicFormSettings {
  emailEnabled: boolean;
  emailTemplate: string;
  smsEnabled: boolean;
  smsTemplate: string;
  showCreationDate: boolean;
  showVisitDate: boolean;
  defaultLocale?: string;
  customWelcome?: string;
  clinicManageLocale?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const ClinicCustomization = ({ ...props }: ClinicCustomizationProps) => {
  const [clinicName, setClinicName] = useState<string>(null);
  const [clinicDisplayName, setClinicDisplayName] = useState<string>(null);
  const [orgClinicDisplayName, setOrgClinicDisplayName] = useState<string>(
    null,
  );
  // const [configuration, setConfiguration] = useState<ClinicGeneralOptions>(
  //   null,
  // );
  const [settings, setSettings] = useState<ClinicFormSettings>(null);
  const [orgSettings, setOrgSettings] = useState<ClinicFormSettings>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [resetTemplateDialog, setResetTemplateDialog] = useState<
    TemplateDialogProps
  >(null);
  const [dirtyName, setDirtyName] = useState<boolean>(false);
  const [dirtySettings, setDirtySettings] = useState<boolean>(false);
  const [clinicCustomTheme, setClinicCustomTheme] = useState<ThemeSettings>(
    require("../clinicSettings/defaultSettings/theme.json"),
  );
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  const [
    { clinicData, clinicConfig, defaultLocale, isMasterClinic },
    setClinicData,
  ] = useClinicState();

  const { message, setMessage } = useClinicMessagesContext();

  const { hasManagerAccess } = useClinicUserContext();

  const clinicId = clinicData.id;
  const { config, isReady } = useUpdateClinicConfig();
  const { translate } = useTranslateByGender();
  const DEFAULT_CUSTOM_WELCOME = translate("WELCOME_PARTNERS_2"); //(!) this key/value exists both in "ask" and in "patient" translation files
  const dao = useClinicContext();
  const params = useParams();
  const currentTab = params?.["tab"] || CustomizationTabs.General;
  const match = useRouteMatch();
  const history = useHistory();
  const allowUsers = hasManagerAccess;

  const baseLocale = defaultLocale.split("_")[0]; //remove county suffix

  const handleTabChange = (tab: CustomizationTabs) => {
    const removeTabName = (path) => {
      return path.substr(0, removeTrailingSlash(path).lastIndexOf("/:tab"));
    };
    history.push(`${removeTabName(match.path)}/${tab}`);
  };

  const handleCloseTemplateDialog = () => {
    setResetTemplateDialog(null);
  };

  const handleNameChange = (newName) => {
    setClinicDisplayName(newName);
    // setClinicName(
    //   encodeURIComponent(newName.toLowerCase()).replaceAll("%20", "_"),
    // );
  };

  const handleClinicLocaleChange = (newClinicLocale) =>
    setSettings((old) => ({
      ...old,
      clinicManageLocale: newClinicLocale === "" ? null : newClinicLocale,
    }));

  const handleLocaleChange = (newLocale) => {
    setSettings((oldSettings) => {
      return {
        ...oldSettings,
        defaultLocale: newLocale,
      };
    });
  };

  const createTemplate = (template: string) => {
    return {
      touchPoint: "send-conversation-link",
      templateId: "",
      inlineTemplate: template,
      language: defaultLocale,
    };
  };

  const showErrorToast = () => {
    setMessage({
      text: "Failed to change clinic settings!",
      severity: "error",
    });
  };

  const renameClinic = () =>
    new Promise<any>((resolve, reject) =>
      dao
        .renameClinic(clinicId, clinicName, clinicDisplayName)
        .then((renamed) => {
          if (renamed === "success") {
            resolve(renamed);
            setClinicData((oldData) => {
              return {
                ...oldData,
                clinicData: {
                  ...oldData.clinicData,
                  displayName: clinicDisplayName,
                },
              };
            }); //saving to Top context (ClinicStateData)
            evt({
              category: CATEGORY.CLINIC_MANAGEMENT,
              action: ACTION.CLINIC_SAVE_CUSTOM_CHANGES,
              props: {
                clinicName,
                changedField: "rename clinic",
              },
            });
          } else {
            setMessage({
              text: "Failed to change clinic name!",
              severity: "error",
            });
            reject();
          }
        })
        .catch((error) => {
          console.error(error);
          setMessage({
            text: "Failed to change clinic name!",
            severity: "error",
          });
          reject();
        }),
    );

  const updateClinicSettings = () =>
    new Promise<any>((resolve, reject) => {
      try {
        const updatedConfigString = {
          ...clinicData.config["unifiedConfigString"],
        };

        if (
          settings.emailTemplate &&
          settings.emailTemplate !== DEFAULT_EMAIL_INLINE_TEMPLATE
        ) {
          //update or create email template
          updatedConfigString["backoffice.email.emailTemplates"] = [
            createTemplate(settings.emailTemplate),
          ];
          evt({
            category: CATEGORY.CLINIC_MANAGEMENT,
            action: ACTION.CLINIC_SAVE_CUSTOM_CHANGES,
            props: {
              clinicName,
              changedField: "backoffice.email.emailTemplates",
            },
          });
        } else {
          //remove email templates
          delete updatedConfigString["backoffice.email.emailTemplates"];
        }

        if (
          settings.smsTemplate &&
          settings.smsTemplate !== DEFAULT_SMS_INLINE_TEMPLATE
        ) {
          //update or create sms template
          updatedConfigString["backoffice.sms.templates"] = [
            createTemplate(settings.smsTemplate),
          ];
          evt({
            category: CATEGORY.CLINIC_MANAGEMENT,
            action: ACTION.CLINIC_SAVE_CUSTOM_CHANGES,
            props: {
              clinicName,
              changedField: "backoffice.sms.templates",
            },
          });
        } else {
          //remove sms templates
          delete updatedConfigString["backoffice.sms.templates"];
        }

        if (settings.defaultLocale) {
          updatedConfigString["clinic.defaultLocale"] = settings.defaultLocale;
          evt({
            category: CATEGORY.CLINIC_MANAGEMENT,
            action: ACTION.CLINIC_SAVE_CUSTOM_CHANGES,
            props: {
              clinicName,
              changedField: "clinic.defaultLocale",
            },
          });
        }

        if (settings.clinicManageLocale) {
          updatedConfigString["clinic.clinicManageLocale"] =
            settings.clinicManageLocale;
          evt({
            category: CATEGORY.CLINIC_MANAGEMENT,
            action: ACTION.CLINIC_SAVE_CUSTOM_CHANGES,
            props: {
              clinicName,
              changedField: "clinic.clinicManageLocale",
            },
          });
        }
        const updatedConfig = { ...clinicConfig };
        updatedConfig["unifiedConfigString"] = {
          ...updatedConfigString,
          "clinic.standalone.showCreationDate": settings.showCreationDate,
          "clinic.standalone.showVisitDate": settings.showVisitDate,
        };

        if (
          settings.customWelcome &&
          settings.customWelcome !== DEFAULT_CUSTOM_WELCOME
        ) {
          evt({
            category: CATEGORY.CLINIC_MANAGEMENT,
            action: ACTION.CLINIC_SAVE_CUSTOM_CHANGES,
            props: {
              clinicName,
              changedField: "WELCOME_PARTNERS_2",
            },
          });
          //update or create
          if (updatedConfig["translations"]?.[baseLocale]) {
            updatedConfig["translations"][baseLocale]["WELCOME_PARTNERS_2"] =
              settings.customWelcome;
          } else {
            updatedConfig["translations"] = {
              ...updatedConfig["translations"],
              [baseLocale]: {
                basedOn: baseLocale,
                WELCOME_PARTNERS_2: settings.customWelcome,
              },
            };
          }
        } else {
          //remove
          delete updatedConfig["translations"]?.[baseLocale]?.[
            "WELCOME_PARTNERS_2"
          ];
        }

        dao
          .setClinicConfig(clinicId, updatedConfig)
          .then((updateResult) => {
            if (updateResult === "success") {
              setClinicData((oldData) => {
                return {
                  ...oldData,
                  clinicData: {
                    ...oldData.clinicData,
                    config: {
                      ...oldData?.clinicData?.config,
                      ...updatedConfig,
                    },
                  },
                  defaultLocale:
                    updatedConfig["unifiedConfigString"][
                      "clinic.defaultLocale"
                    ],
                };
              }); //saving to Top context (ClinicStateData)
              resolve(updateResult);
            } else {
              showErrorToast();
              reject();
            }
          })
          .catch((error) => {
            showErrorToast();
            reject(error);
          });
      } catch (err) {
        showErrorToast();
        reject(err);
      }
    });

  const saveSettings = () => {
    setShowConfirm(false);
    setUpdating(true);
    setMessage(null);
    const updatePromises: Promise<any>[] = [];
    if (dirtyName) updatePromises.push(renameClinic());
    if (dirtySettings) updatePromises.push(updateClinicSettings());

    Promise.all(updatePromises)
      .then(() => {
        if (!message) {
          setMessage({
            text: "Clinic settings saved successfully",
            severity: "success",
          });
          //console.log("saved:", settings);
          //setOrgSettings({ ...settings });
          // if (props.onNameChange)
          //   props.onNameChange(clinicId, clinicName, clinicDisplayName); //update name in clinic header tab and clinic selection list
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setUpdating(false));
  };

  useEffect(() => {
    if (isReady) {
      setClinicName(clinicData.name);
      setClinicDisplayName(clinicData.displayName);
      setOrgClinicDisplayName(clinicData.displayName);

      const clinicSettings = getV2ClinicSettings(config);

      //get partial color theme for preview
      setClinicCustomTheme((prevState) => {
        return {
          ...prevState,
          gradient: clinicSettings["theme.gradient"] ?? prevState.gradient,
          colors: {
            ...prevState.colors,
            "--primary":
              clinicSettings["theme.colors.--primary"] ||
              prevState.colors["--primary"],
            "--grad-light":
              clinicSettings["theme.colors.--grad-light"] ||
              prevState.colors["--grad-light"],
            "--grad-dark":
              clinicSettings["theme.colors.--grad-dark"] ||
              prevState.colors["--grad-dark"],
            "--homepage-link-color":
              clinicSettings["theme.colors.--homepage-text-color"] ||
              prevState.colors["--homepage-text-color"],
            "--button-text-color":
              clinicSettings["theme.colors.--button-text-color"] ||
              prevState.colors["--button-text-color"],
          },
          homepageBackgroundImageUrl: clinicSettings["theme.backgroundimg"],
        };
      });

      const configString = clinicData.config["unifiedConfigString"];
      //find email templates
      const emailTemplates =
        configString && configString["backoffice.email.emailTemplates"];
      //find sms templates
      const smsTemplates =
        configString && configString["backoffice.sms.templates"];

      const customWelcome =
        clinicData.config["translations"]?.[baseLocale]?.[
          "WELCOME_PARTNERS_2"
        ] || DEFAULT_CUSTOM_WELCOME;

      const initialSettings: ClinicFormSettings = {
        emailEnabled: true,
        emailTemplate: emailTemplates?.length
          ? emailTemplates[0].inlineTemplate
          : DEFAULT_EMAIL_INLINE_TEMPLATE,
        smsEnabled: true,
        smsTemplate: smsTemplates?.length
          ? smsTemplates[0].inlineTemplate
          : DEFAULT_SMS_INLINE_TEMPLATE,
        showCreationDate: clinicSettings["clinic.standalone.showCreationDate"],
        showVisitDate: clinicSettings["clinic.standalone.showVisitDate"],
        defaultLocale: clinicSettings["clinic.defaultLocale"],
        clinicManageLocale: clinicSettings["clinic.clinicManageLocale"],
        customWelcome,
      };

      setSettings({ ...initialSettings });
      setOrgSettings({ ...initialSettings });
    }
  }, [
    isReady,
    config,
    clinicData,
    defaultLocale,
    baseLocale,
    DEFAULT_CUSTOM_WELCOME,
  ]);

  //Dirty indicators
  useEffect(() => {
    if (!settings || !orgSettings) return;
    //using 2 dirty indicator because the clinic name is saved separately from the rest of the settings
    setDirtyName(clinicDisplayName !== orgClinicDisplayName);
    setDirtySettings(
      Object.keys(settings).some((s) => settings[s] !== orgSettings[s]),
    );
  }, [settings, clinicDisplayName, orgSettings, orgClinicDisplayName]);

  const invalid = !clinicDisplayName;
  const rtlView = settings?.defaultLocale === "he";

  if (!settings)
    return (
      <ClinicPageWrapper
        style={{
          alignItems: "center",
          marginTop: 20,
        }}
      >
        <LoadingProgress label={"Loading clinic settings"} />
      </ClinicPageWrapper>
    );

  return (
    <ClinicPageWrapper
      className="clinic-customization"
      pageTitle={
        <>
          <CustomizationTabsSelect
            value={currentTab}
            aria-label="customization tabs"
          >
            <CustomizationTab
              disableRipple
              label="General Settings"
              value={CustomizationTabs.General}
              onClick={() => handleTabChange(CustomizationTabs.General)}
            />
            <CustomizationTab
              disableRipple
              label="Text Customization"
              value={CustomizationTabs.TextCustomization}
              onClick={() =>
                handleTabChange(CustomizationTabs.TextCustomization)
              }
            />
            {allowUsers && (
              <CustomizationTab
                disableRipple
                label="User Management"
                value={CustomizationTabs.UserManagement}
                onClick={() =>
                  handleTabChange(CustomizationTabs.UserManagement)
                }
              />
            )}
          </CustomizationTabsSelect>
          <ClinicActionButton
            onClick={() =>
              isMasterClinic ? setShowConfirm(true) : saveSettings()
            }
            label={updating ? "Saving" : "Save Changes"}
            icon={updating && <CircularProgress size={20} />}
            style={{ marginLeft: "auto" }}
            disabled={updating || invalid || !(dirtyName || dirtySettings)}
          />
        </>
      }
      pageTitleStyle={{
        paddingBottom: 0,
      }}
    >
      {currentTab === CustomizationTabs.General && (
        <ClinicCustomizationGeneral
          settings={settings}
          clinicDisplayName={clinicDisplayName}
          handleSettingsChange={(updatedSetting) => setSettings(updatedSetting)}
          handleNameChange={(updatedName) => handleNameChange(updatedName)}
          handleLocaleChange={(newLocale) => handleLocaleChange(newLocale)}
          handleClinicLocaleChange={(c) => handleClinicLocaleChange(c)}
          handelTemplateReset={(template) => setResetTemplateDialog(template)}
          rtlView={rtlView}
          supportedLanguages={clinicConfig.supportedLanguages}
        />
      )}
      {currentTab === CustomizationTabs.TextCustomization && (
        <ClinicCustomizationTexts
          settings={settings}
          translate={translate}
          customTheme={clinicCustomTheme}
          handleSettingsChange={(updatedSetting) => setSettings(updatedSetting)}
          handelTemplateReset={(template) => setResetTemplateDialog(template)}
          defaultWelcomeText={DEFAULT_CUSTOM_WELCOME}
          rtlView={rtlView}
        />
      )}
      {currentTab === CustomizationTabs.UserManagement && allowUsers && (
        <ClinicUsers />
      )}
      {/*----- Reset text dialog ----*/
      (currentTab === CustomizationTabs.General ||
        currentTab === CustomizationTabs.TextCustomization) && (
        <ClinicDialog
          open={!!resetTemplateDialog}
          onClose={() => handleCloseTemplateDialog()}
          closeButton={false}
        >
          <DialogContent>
            Are you sure you want to reset your{" "}
            {resetTemplateDialog?.methodName} template to it's default?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => handleCloseTemplateDialog()}
              color="secondary"
              autoFocus
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setSettings((prevState) => {
                  prevState[resetTemplateDialog?.templateName] =
                    resetTemplateDialog?.templateDefault;
                  return { ...prevState };
                });
                handleCloseTemplateDialog();
              }}
              color="primary"
            >
              OK
            </Button>
          </DialogActions>
        </ClinicDialog>
      )}
      {isMasterClinic && (
        <ClinicDialog
          onClose={() => setShowConfirm(false)}
          closeButton={false}
          open={showConfirm}
        >
          <MasterClinicWarning />
          <DialogActions>
            <Button
              onClick={() => setShowConfirm(false)}
              color="secondary"
              autoFocus
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                saveSettings();
              }}
              color="primary"
            >
              OK
            </Button>
          </DialogActions>
        </ClinicDialog>
      )}
    </ClinicPageWrapper>
  );
};
